import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Server, HardDrive, Activity, DollarSign } from "lucide-react";

function fmtBytes(n) {
  if (!n) return "0 B";
  const units = ["B","KB","MB","GB","TB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length-1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

const fmtUSD = n => "$" + Number(n || 0).toFixed(n < 1 ? 4 : 2);

export default function InfraCostMonitor() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => { api.get("/admin/infra", { params: { days } }).then(r => setData(r.data)); }, [days]);
  if (!data) return <div className="p-6 text-ink-soft">Loading…</div>;

  // Convert series bytes to MB for chart readability
  const series = data.series.map(s => ({
    day: s.day,
    db_mb: +(s.db_bytes / (1024 * 1024)).toFixed(2),
    evidence_mb: +(s.evidence_bytes / (1024 * 1024)).toFixed(3),
    requests: s.requests_today,
    cost: s.daily_cost_usd,
  }));

  return (
    <div className="p-6 space-y-5" data-testid="infra-root">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="section-label">Admin · Infrastructure</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1">Infra Cost Monitor</h1>
          <p className="text-sm text-ink-soft">
            Storage and request volume per tenant, with a transparent unit-economics cost model.
          </p>
          {data.synthetic_rows > 0 && (
            <div className="mt-2 inline-flex items-center gap-2 pill pill-warn text-[11px]" data-testid="infra-synthetic-pill">
              {data.synthetic_rows} synthetic backfill {data.synthetic_rows === 1 ? "row" : "rows"} ·
              {" "}{data.real_rows} real-telemetry {data.real_rows === 1 ? "row" : "rows"}
            </div>
          )}
        </div>
        <select data-testid="infra-days-select" value={days} onChange={e=>setDays(Number(e.target.value))} className="border border-border rounded-md px-3 py-2 text-sm">
          {[7, 14, 30, 60, 90].map(d => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { l: "DB size", v: fmtBytes(data.now.db_bytes), i: HardDrive, a: "bg-brand" },
          { l: "Evidence size", v: fmtBytes(data.now.evidence_bytes), i: Server, a: "bg-status-info" },
          { l: "Requests (today)", v: data.now.requests_today.toLocaleString(), i: Activity, a: "bg-status-warning" },
          { l: "Projected month", v: fmtUSD(data.projected_monthly_cost_usd), i: DollarSign, a: "bg-status-success" },
        ].map(m => {
          const Icon = m.i;
          return (
            <div key={m.l} className="card p-4" data-testid={`infra-tile-${m.l.toLowerCase().replace(/\s/g,'-').replace(/[()]/g,'')}`}>
              <div className="flex items-center justify-between">
                <div className="section-label">{m.l}</div>
                <div className={"h-7 w-7 rounded-md text-white flex items-center justify-center " + m.a}><Icon size={14}/></div>
              </div>
              <div className="metric-value">{m.v}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-4">
          <div className="section-label">Storage growth (MB)</div>
          <div className="h-[260px] mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="db-g" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#064E3B" stopOpacity={0.3}/>
                    <stop offset="100%" stopColor="#064E3B" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="ev-g" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E2E8F0" vertical={false}/>
                <XAxis dataKey="day" tick={{fontSize:10, fill:'#64748B'}} tickFormatter={d => d.slice(5)} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontSize:10, fill:'#64748B'}} axisLine={false} tickLine={false} width={50}/>
                <Tooltip />
                <Legend wrapperStyle={{fontSize: 11}}/>
                <Area type="monotone" dataKey="db_mb" stroke="#064E3B" fill="url(#db-g)" strokeWidth={2} name="DB (MB)"/>
                <Area type="monotone" dataKey="evidence_mb" stroke="#3B82F6" fill="url(#ev-g)" strokeWidth={2} name="Evidence (MB)"/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-4">
          <div className="section-label">Daily cost (USD)</div>
          <div className="h-[260px] mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="cost-g" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.4}/>
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E2E8F0" vertical={false}/>
                <XAxis dataKey="day" tick={{fontSize:10, fill:'#64748B'}} tickFormatter={d => d.slice(5)} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontSize:10, fill:'#64748B'}} axisLine={false} tickLine={false} width={60}/>
                <Tooltip />
                <Area type="monotone" dataKey="cost" stroke="#10B981" fill="url(#cost-g)" strokeWidth={2}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="card p-4" data-testid="infra-pricing">
        <div className="section-label">Pricing model</div>
        <div className="mt-2 grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <div><div className="text-ink-soft text-xs">DB storage</div><div className="font-mono">${data.pricing.db_storage_per_gb_month}/GB/mo</div></div>
          <div><div className="text-ink-soft text-xs">Evidence storage</div><div className="font-mono">${data.pricing.evidence_storage_per_gb_month}/GB/mo</div></div>
          <div><div className="text-ink-soft text-xs">Requests</div><div className="font-mono">${data.pricing.request_per_million}/M</div></div>
          <div><div className="text-ink-soft text-xs">Egress</div><div className="font-mono">${data.pricing.egress_per_gb}/GB</div></div>
        </div>
      </div>
    </div>
  );
}
